import { DIRECT_SUBMISSION_PERMISSIONS, WILDCARD } from '../components/directSubmissionPermissions/constants';

const EMPTY_ACCESS = Object.freeze({
    isInitiator: false,
    isApprover: false,
    initiatorScopes: [],
    approverScopes: [],
});

const CONDITION_PATTERN = /companyId\s+EQ\s+'([^']+)'\s+AND\s+countryCode\s+EQ\s+'([^']+)'/gi;

/**
 * Parses an IAM grant condition string into company/country scope pairs.
 * Handles the OR-joined multi-company form produced by the admin UI.
 */
export const parseConditionScopes = (condition) => {
    if (!condition) {
        return [];
    }
    const scopes = [];
    let match;
    CONDITION_PATTERN.lastIndex = 0;
    while ((match = CONDITION_PATTERN.exec(condition)) !== null) {
        scopes.push({ companyId: match[1], countryCode: match[2] });
    }
    return scopes;
};

const readGrantBlocks = (jwtPayload, iamNamespace) => {
    const rawGrants = jwtPayload && jwtPayload['t-grt'];
    if (!rawGrants) {
        return [];
    }
    const parsed = JSON.parse(rawGrants);
    return parsed[iamNamespace] || [];
};

const findBlockWithPermission = (blocks, permission) =>
    blocks.find((block) => Array.isArray(block.p) && block.p.includes(permission));

/**
 * Extracts Direct Submission initiator/approver grants and their company/country
 * scopes from a decoded JWT payload. Fails closed: any missing or malformed
 * `t-grt` claim yields empty (no-access) scopes.
 */
export const parseDirectSubmissionGrants = (jwtPayload, iamNamespace) => {
    try {
        const blocks = readGrantBlocks(jwtPayload, iamNamespace);
        const initiatorBlock = findBlockWithPermission(blocks, DIRECT_SUBMISSION_PERMISSIONS.INITIATE);
        const approverBlock = findBlockWithPermission(blocks, DIRECT_SUBMISSION_PERMISSIONS.APPROVE);

        return {
            isInitiator: Boolean(initiatorBlock),
            isApprover: Boolean(approverBlock),
            initiatorScopes: initiatorBlock ? parseConditionScopes(initiatorBlock.c) : [],
            approverScopes: approverBlock ? parseConditionScopes(approverBlock.c) : [],
        };
    } catch {
        return { ...EMPTY_ACCESS };
    }
};

const dimensionMatches = (scopeValue, target) =>
    scopeValue === WILDCARD || scopeValue === String(target);

const countryMatches = (scopeValue, target) =>
    scopeValue === WILDCARD || scopeValue.toUpperCase() === String(target).toUpperCase();

const scopeAllows = (scopes, companyId, countryCode) =>
    scopes.some((scope) =>
        dimensionMatches(scope.companyId, companyId) &&
        countryMatches(scope.countryCode, countryCode)
    );

/**
 * True when the user may initiate a direct submission for the given
 * company + country, honouring the `_all_` wildcard on either dimension.
 */
export const canInitiateDirectSubmission = (dsAccess, companyId, countryCode) =>
    Boolean(dsAccess?.isInitiator) && scopeAllows(dsAccess.initiatorScopes || [], companyId, countryCode);

/**
 * Gates initiate enforcement behind a feature flag. When enforcement is off,
 * the action is always allowed (pre-enforcement behaviour). When on, it defers
 * to the JWT-scope check.
 */
export const canInitiateDirectSubmissionGated = (enforcePermissions, dsAccess, companyId, countryCode) =>
    !enforcePermissions || canInitiateDirectSubmission(dsAccess, companyId, countryCode);

/**
 * True when the user may approve a direct submission for the given
 * company + country, honouring the `_all_` wildcard on either dimension.
 */
export const canApproveDirectSubmission = (dsAccess, companyId, countryCode) =>
    Boolean(dsAccess?.isApprover) && scopeAllows(dsAccess.approverScopes || [], companyId, countryCode);
